<script lang="ts">
export default {
    name: 'ConnectionGenerateLink',
};
</script>

<script setup lang="ts">
import { watch, ref, computed } from 'vue';
import { useClipboard } from '@vueuse/core';
import { ApiError } from '@/api/http-error';
import i18n from '@/plugins/i18n';
import { connectionLink } from '@/modules/connection/api';
import { CONNECTION } from '@/store/modules/acl-access/constants';
import type { ConnectionDirection, ConnectionId, ConnectionOrigin } from '@/modules/connection/types/connection';

// @ts-expect-error not typed yet
import { useStore } from '@/store/index.js';
// @ts-expect-error not typed yet
import message from '@/components/base-components/components/message/message.js';
// @ts-expect-error not typed yet
import notify from '@/components/base-components/components/notification/notify.js';

const props = defineProps<{
    platform?: string;
    callback: (data: { platform: string }) => Promise<{
        id: ConnectionId;
        direction: ConnectionDirection;
        origin: ConnectionOrigin;
    }>;
}>();

const store = useStore();

const hasAccess = computed(() => store.getters['aclAccess/isGranted'](CONNECTION.LINK));

const loading = ref(false);

const link = ref();

const { copy, copied } = useClipboard();

watch(loading, (value) => {
    if (value) {
        store.dispatch('loader/SHOW');
    } else {
        store.dispatch('loader/HIDE');
    }
});

const tooltip = computed(() => {
    if (!props.platform) return 'Select a platform to generate link';

    return 'Generate a secure link and send it to an authorized user so they can approve the connection';
});

async function onClick() {
    if (copied.value || !props.platform) return;

    if (link.value) {
        await copy(link.value);

        return;
    }

    loading.value = true;

    try {
        const { id, direction, origin } = await props.callback({ platform: props.platform });

        const data = await connectionLink({ id, direction, origin });

        link.value = data.link;

        await copy(link.value);

        message.success('Link copied to clipboard');
    } catch (error) {
        if (error instanceof ApiError) {
            notify.error({
                title: i18n.t('tmp An error occurred'),
                message: error.message,
                duration: 7500,
            });

            return;
        }

        throw error;
    } finally {
        loading.value = false;
    }
}
</script>

<template>
    <base-tooltip
        v-if="hasAccess"
        :content="tooltip"
        placement="top"
    >
        <base-button
            :icon="{
                class: 'h-4 shrink-0',
                ...(copied ? { name: 'check' } : { name: 'link' })
            }"
            :label="copied ? 'Link Copied' : 'Generate Link'"
            :type="copied ? 'success' : 'default'"
            :disabled="!platform"
            :loading="loading"
            size="small"
            round
            text
            @click="onClick"
        />
    </base-tooltip>
</template>
